import { useState, type FormEvent } from 'react';
import { AlertCircle, Mail } from 'lucide-react';
import { useRequestOtp, useVerifyOtp } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';

// New starters open the sign-in link from an email, which on Android lands in
// the Gmail in-app browser. Reading the 6-digit code means leaving that WebView,
// and coming back remounts the page fresh. We persist a tiny hint so a reopened
// WebView resumes at the code step with the email remembered, instead of forcing
// a fresh request that would invalidate the code already sitting in the inbox.
const OTP_PENDING_KEY = 'hcc:onboarding:otp-pending';
// Must match OTP_TTL_MS in apps/api/src/services/auth/otp.ts (10 minutes). After
// this the server has expired the code, so resuming to it would only frustrate.
const OTP_TTL_MS = 10 * 60 * 1000;

type OtpPending = { email: string; requestedAt: number };

function readOtpPending(): OtpPending | null {
  try {
    const raw = window.localStorage.getItem(OTP_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OtpPending>;
    if (typeof parsed.email !== 'string' || typeof parsed.requestedAt !== 'number') {
      return null;
    }
    if (Date.now() - parsed.requestedAt > OTP_TTL_MS) return null;
    return { email: parsed.email, requestedAt: parsed.requestedAt };
  } catch {
    // localStorage can throw in private-mode WebViews; just skip the resume.
    return null;
  }
}

function writeOtpPending(email: string): void {
  try {
    window.localStorage.setItem(
      OTP_PENDING_KEY,
      JSON.stringify({ email, requestedAt: Date.now() }),
    );
  } catch {
    // Non-fatal: the flow still works, it just won't resume after a reload.
  }
}

function clearOtpPending(): void {
  try {
    window.localStorage.removeItem(OTP_PENDING_KEY);
  } catch {
    // Ignore; nothing to clean up if storage is unavailable.
  }
}

/**
 * Personal-email one-time-code sign in. Two steps in one card: enter the
 * email HR has on file, then the 6-digit code sent to it. This is the
 * route a new starter uses before they have a Corrigenda account.
 */
export function OtpPanel() {
  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const pending = readOtpPending();
  const [stage, setStage] = useState<'email' | 'code'>(pending ? 'code' : 'email');
  const [email, setEmail] = useState(pending?.email ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Set when the server refuses a valid code because the account is closed:
  // either the person is archived/deleted (contact HR) or the account was
  // blocked by an admin (contact your administrator). Distinct from `error`
  // so we replace the form with a clear panel instead of the amber "that
  // code did not work" line.
  const [blocked, setBlocked] = useState<{ title: string; body: string } | null>(null);

  async function onRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await requestOtp.mutateAsync({ email });
      writeOtpPending(email);
      setStage('code');
    } catch {
      // Server always 200s; a thrown error here is a network fault.
      setError('Could not send a code. Check your connection and try again.');
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await verifyOtp.mutateAsync({ email, code });
      clearOtpPending();
      window.location.href = '/';
    } catch (err) {
      // The code was right but the account is closed: stop the login flow and
      // show the appropriate panel, not a retry. Archived person -> HR;
      // admin-blocked account -> administrator.
      if (err instanceof ApiError && err.code === 'AccountInactiveError') {
        clearOtpPending();
        setBlocked({
          title: 'This account is no longer active',
          body: 'Your access to onboarding has been closed. If you think this is a mistake, please contact HR.',
        });
        return;
      }
      if (err instanceof ApiError && err.code === 'AccountDisabledError') {
        clearOtpPending();
        setBlocked({
          title: 'This account has been disabled',
          body: 'Sign-in for this account has been blocked. Please contact your administrator.',
        });
        return;
      }
      setError(
        err instanceof ApiError
          ? err.message
          : 'That code did not work. Request a new one and try again.',
      );
    }
  }

  if (blocked) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-md bg-warning-soft/15 px-3 py-3 text-sm text-warning-soft-fg">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">{blocked.title}</p>
            <p>{blocked.body}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setBlocked(null);
            setStage('email');
            setEmail('');
            setCode('');
            setError(null);
          }}
          className="block w-full text-center text-xs text-fg-muted underline-offset-2 hover:underline"
        >
          Use a different email
        </button>
      </div>
    );
  }

  if (stage === 'email') {
    return (
      <form onSubmit={onRequest} className="space-y-4">
        <label className="block text-sm">
          <span className="font-medium text-fg-muted">Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="you@personal-email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 block h-11 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
        </label>
        {error ? (
          <div className="flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          iconLeft={<Mail className="h-4 w-4" />}
          loading={requestOtp.isPending}
        >
          {requestOtp.isPending ? 'Sending...' : 'Email me a sign-in code'}
        </Button>
        <p className="text-center text-xs text-fg-muted">
          Onboarding? Use the email address your contact used to reach you.
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={onVerify} className="space-y-4">
      {/* "If on file" wording is deliberate: the server answers 200 for
          unknown emails too (anti-enumeration), so we must not claim a
          code was definitely sent. */}
      <p className="text-sm text-fg-muted">
        If <span className="font-medium text-fg">{email}</span> is on file, we have emailed
        it a 6-digit sign-in code. Nothing after a minute or two? Check your spam folder
        and the spelling of your email, or speak to your HR representative.
      </p>
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Sign-in code</span>
        <input
          // A reopened WebView lands here ready to type the code without an extra tap.
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          pattern="\d{6}"
          maxLength={6}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="mt-1.5 block h-11 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-center text-lg tracking-[0.4em] shadow-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
        />
      </label>
      {error ? (
        <div className="flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={verifyOtp.isPending}
      >
        {verifyOtp.isPending ? 'Checking...' : 'Sign in'}
      </Button>
      <button
        type="button"
        onClick={() => {
          clearOtpPending();
          setStage('email');
          setCode('');
          setError(null);
        }}
        className="block w-full text-center text-xs text-fg-muted underline-offset-2 hover:underline"
      >
        Use a different email or request a new code
      </button>
    </form>
  );
}
